/**
 * @voltxampere/web — `<vxa-settings-data>` Veri sekmesi (Sprint 21 C5)
 *
 * AYARLAR pane's 7th sub-tab (D-21-3 free-form per Şefim approval —
 * mockup canonical doesn't include this tab; layout follows the
 * Sprint 18 sticky-nav + content-card idiom).
 *
 * Three sections:
 *   1. Storage gauge — total bytes used + circuit count
 *   2. Recent circuits — N=8 grid (Sprint 21 D-21-6) with click-to-load
 *   3. Import .vxa + Clear all data — destructive action behind confirm
 *
 * Subscribes to `currentCircuit` (boot pushRecent) and refreshes the
 * recents + storage gauge whenever a save settles. Sprint 22+ may
 * upgrade the UI to match a future canonical mockup.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { LitElement, css, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { effect } from "@preact/signals-core";

import {
  clearCircuits,
  clearRecents,
  // Sprint 24 A0 (bug_002 fix) — settings store wipe.
  clearSettings,
  deleteCircuit,
  fromVXA,
  getCircuit,
  listRecents,
  pushRecent,
  putCircuit,
  removeRecent,
  storageUsage,
  VxaFormatError,
  type RecentEntry,
  type StorageUsage,
} from "@voltxampere/persistence";

import { clearAllRecentCommands } from "../commands/recent.js";
import { clearAllBookmarks } from "../store/bookmarks-persistence.js";

import { autoSaveLastSavedAt } from "../store/auto-save.js";
import { currentCircuit } from "../store/circuit.js";
import { setPane } from "../store/router.js";
import { t } from "../i18n/index.js";
// Sprint 24 A0 (bug_002 fix) — in-memory signal reset on clearAll
// so a fresh boot does not re-write the just-cleared values.
import { tourShownPersisted } from "../onboarding/tour-state.js";
import {
  canvasStyle,
  setCanvasStyle,
  setSymbolStandard,
  setWireCurve,
} from "../store/canvas-prefs.js";
import { resetAllOverrides, shortcutOverrides } from "../store/shortcut-overrides.js";

@customElement("vxa-settings-data")
export class VxaSettingsData extends LitElement {
  @state() private _usage: StorageUsage = { bytesUsed: 0, circuitCount: 0 };
  @state() private _recents: readonly RecentEntry[] = [];
  @state() private _busy = false;
  @state() private _toast: string | null = null;
  @state() private _confirmingClear = false;

  private cleanup: (() => void) | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    void this.refresh();
    // Re-load whenever an auto-save lands (timestamp changes).
    this.cleanup = effect(() => {
      void autoSaveLastSavedAt.value;
      void this.refresh();
    });
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.cleanup?.();
    this.cleanup = null;
  }

  static override styles = css`
    :host {
      display: block;
    }
    .section {
      margin-bottom: 32px;
    }
    .section-title {
      font-family: var(--font-mono, "JetBrains Mono", ui-monospace, monospace);
      font-size: 11px;
      letter-spacing: 0.3em;
      color: var(--brass, #c08a3e);
      text-transform: uppercase;
      margin-bottom: 12px;
    }
    .gauge {
      display: flex;
      gap: 32px;
      align-items: baseline;
      padding: 16px 20px;
      border: 1px solid rgba(180, 180, 200, 0.18);
      border-radius: 6px;
      background: rgba(8, 12, 23, 0.4);
    }
    .gauge-num {
      font-family: var(--font-display, "Cormorant Infant", serif);
      font-size: 28px;
      color: var(--brass, #c08a3e);
    }
    .gauge-label {
      font-family: var(--font-mono, ui-monospace, monospace);
      font-size: 11px;
      color: var(--ivory-dim, rgba(220, 220, 230, 0.55));
      letter-spacing: 0.1em;
      text-transform: uppercase;
    }
    .grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;
    }
    .card {
      padding: 14px 16px;
      border: 1px solid rgba(180, 180, 200, 0.18);
      border-radius: 6px;
      cursor: pointer;
      background: rgba(8, 12, 23, 0.4);
      text-align: left;
      color: inherit;
      font-family: inherit;
    }
    .card:hover {
      border-color: var(--brass, #c08a3e);
    }
    .card-name {
      font-family: var(--font-display, serif);
      font-size: 16px;
      color: var(--ivory, #f3ead5);
      margin-bottom: 4px;
    }
    .card-meta {
      font-family: var(--font-mono, monospace);
      font-size: 10px;
      color: var(--ivory-dim, rgba(220, 220, 230, 0.55));
      letter-spacing: 0.08em;
    }
    .card-actions {
      margin-top: 8px;
      display: flex;
      gap: 8px;
    }
    .card-actions button {
      font-family: var(--font-mono, monospace);
      font-size: 10px;
      padding: 4px 8px;
      background: transparent;
      border: 1px solid rgba(220, 220, 230, 0.3);
      color: var(--ivory-dim);
      cursor: pointer;
      border-radius: 3px;
    }
    .card-actions button:hover {
      color: var(--brass, #c08a3e);
      border-color: var(--brass, #c08a3e);
    }
    .empty {
      padding: 24px;
      text-align: center;
      color: var(--ivory-dim);
      font-style: italic;
    }
    .actions-row {
      display: flex;
      gap: 12px;
      flex-wrap: wrap;
    }
    button.action {
      padding: 10px 18px;
      font-family: var(--font-mono, monospace);
      font-size: 12px;
      letter-spacing: 0.1em;
      background: transparent;
      border: 1px solid rgba(180, 180, 200, 0.3);
      color: var(--ivory);
      cursor: pointer;
      border-radius: 4px;
    }
    button.action:hover {
      border-color: var(--brass, #c08a3e);
      color: var(--brass, #c08a3e);
    }
    button.action.danger:hover {
      border-color: #b85a4f;
      color: #d97565;
    }
    .toast {
      margin-top: 12px;
      padding: 10px 14px;
      background: rgba(192, 138, 62, 0.12);
      border-left: 2px solid var(--brass, #c08a3e);
      font-family: var(--font-mono, monospace);
      font-size: 11px;
    }
    .confirm {
      padding: 16px;
      border: 1px solid #b85a4f;
      border-radius: 6px;
      background: rgba(184, 90, 79, 0.08);
      margin-top: 12px;
    }
    .confirm-text {
      margin-bottom: 12px;
      font-family: var(--font-body, serif);
    }
    .hidden-input {
      display: none;
    }
  `;

  override render() {
    return html`
      <div class="section">
        <div class="section-title">${t("settings.data.storage.label")}</div>
        <div class="gauge" data-testid="storage-gauge">
          <div>
            <div class="gauge-num">${this._usage.circuitCount}</div>
            <div class="gauge-label">${t("settings.data.storage.circuits")}</div>
          </div>
          <div>
            <div class="gauge-num">${formatBytes(this._usage.bytesUsed)}</div>
            <div class="gauge-label">${t("settings.data.storage.bytes")}</div>
          </div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">${t("settings.data.recents.label")}</div>
        ${this._recents.length === 0
          ? html`<div class="empty">${t("settings.data.recents.empty")}</div>`
          : html`<div class="grid" data-testid="recents-grid">
              ${this._recents.map(
                (r) => html`
                  <div class="card">
                    <div class="card-name">${r.name}</div>
                    <div class="card-meta">${formatDate(r.openedAt)}</div>
                    <div class="card-actions">
                      <button @click=${() => void this.loadRecent(r.id)}>
                        ${t("settings.data.recents.open")}
                      </button>
                      <button @click=${() => void this.deleteRecent(r.id)}>
                        ${t("settings.data.recents.delete")}
                      </button>
                    </div>
                  </div>
                `,
              )}
            </div>`}
      </div>

      <div class="section">
        <div class="section-title">${t("settings.data.actions.label")}</div>
        <div class="actions-row">
          <button
            class="action"
            @click=${() => {
              this.triggerImport();
            }}
            ?disabled=${this._busy}
          >
            ${t("settings.data.import.button")}
          </button>
          <button
            class="action danger"
            @click=${() => {
              this._confirmingClear = true;
            }}
            ?disabled=${this._busy}
          >
            ${t("settings.data.clear.button")}
          </button>
          <input
            class="hidden-input"
            type="file"
            accept=".vxa"
            data-testid="import-file"
            @change=${(e: Event) => void this.handleImportFile(e)}
          />
        </div>
        ${this._confirmingClear
          ? html`
              <div class="confirm" data-testid="clear-confirm">
                <div class="confirm-text">${t("settings.data.clear.confirm")}</div>
                <div class="actions-row">
                  <button class="action danger" @click=${() => void this.clearAll()}>
                    ${t("settings.data.clear.confirm.yes")}
                  </button>
                  <button
                    class="action"
                    @click=${() => {
                      this._confirmingClear = false;
                    }}
                  >
                    ${t("settings.data.clear.confirm.no")}
                  </button>
                </div>
              </div>
            `
          : null}
        ${this._toast !== null ? html`<div class="toast">${this._toast}</div>` : null}
      </div>
    `;
  }

  private async refresh(): Promise<void> {
    try {
      const [usage, recents] = await Promise.all([storageUsage(), listRecents()]);
      this._usage = usage;
      this._recents = recents;
    } catch (err) {
      // IndexedDB unavailable in this environment — render empty state.
      if (import.meta.env.DEV) {
        console.warn("[VXA settings.data] refresh failed:", err);
      }
    }
  }

  private async loadRecent(id: string): Promise<void> {
    this._busy = true;
    try {
      const stored = await getCircuit(id);
      if (!stored) {
        await removeRecent(id);
        this._toast = t("settings.data.import.fail", { reason: id });
        await this.refresh();
        return;
      }
      currentCircuit.value = stored.circuit;
      await pushRecent(stored.id, stored.name);
      setPane("simulator");
    } catch (err) {
      this._toast = t("settings.data.import.fail", {
        reason: err instanceof Error ? err.message : String(err),
      });
    } finally {
      this._busy = false;
      await this.refresh();
    }
  }

  private async deleteRecent(id: string): Promise<void> {
    try {
      await deleteCircuit(id);
      await removeRecent(id);
      await this.refresh();
    } catch (err) {
      this._toast = err instanceof Error ? err.message : String(err);
    }
  }

  private triggerImport(): void {
    const input = this.renderRoot.querySelector<HTMLInputElement>('input[type="file"]');
    input?.click();
  }

  private async handleImportFile(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this._busy = true;
    try {
      const circuit = await fromVXA(file);
      const stored = await putCircuit(circuit);
      await pushRecent(stored.id, stored.name);
      currentCircuit.value = stored.circuit;
      this._toast = t("settings.data.import.success");
      await this.refresh();
    } catch (err) {
      const reason =
        err instanceof VxaFormatError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      this._toast = t("settings.data.import.fail", { reason });
    } finally {
      input.value = ""; // Allow re-importing the same file.
      this._busy = false;
    }
  }

  private async clearAll(): Promise<void> {
    this._confirmingClear = false;
    this._busy = true;
    try {
      await clearCircuits();
      await clearRecents();
      // Sprint 22 C4 — also wipe the command palette Recent ring so
      // "Clear all" really does clear all persisted state.
      await clearAllRecentCommands();
      // Sprint 24 A0 (bug_002 fix) — wipe DB v3 settings store
      // (tour state, custom shortcuts, theme/canvas prefs, locale,
      // AC/transient defaults, a11y). SPRINT_23_REPORT §6 promises
      // "settings hepsi temizlenir; tour reset olur". Without this
      // line, AYARLAR > Veri "Tümünü temizle" left the entire
      // AYARLAR persistence row untouched — bug found by /ultrareview.
      await clearSettings();
      // Sprint 32-4 — also wipe the bookmarks store (DB v4) so the
      // user's per-circuit time scrubber pins disappear with the
      // rest of "Tümünü temizle". Pattern matches the
      // clearAllRecentCommands line above.
      await clearAllBookmarks();
      // Sprint 24 A0 — also reset in-memory signals so the next
      // mergeSettings write does NOT resurrect the stale values
      // from the still-populated reactive state. tourShownPersisted
      // → false makes the next reload re-arm the onboarding tour
      // (canonical "fresh install" UX).
      tourShownPersisted.value = false;
      // Theme defaults (Sprint 18 mockup canon).
      setCanvasStyle("night");
      setWireCurve("catenary");
      setSymbolStandard("iec");
      // Custom shortcut overrides → empty.
      resetAllOverrides();
      // Belt-and-suspenders: ensure the override map is observably
      // empty even if the persister was busy (silently signal
      // mutation).
      shortcutOverrides.value = {};
      // canvasStyle.value usage prevents "imported but unused" lint.
      void canvasStyle.value;
      this._toast = t("settings.data.clear.success");
      await this.refresh();
    } catch (err) {
      this._toast = err instanceof Error ? err.message : String(err);
    } finally {
      this._busy = false;
    }
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)}MB`;
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleString();
}

declare global {
  interface HTMLElementTagNameMap {
    "vxa-settings-data": VxaSettingsData;
  }
}
